/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable import/prefer-default-export */

import logger from 'logger';

export const setSessionItem = <T>(key: string, value: T): void => {
  try {
    const serializedValue = JSON.stringify(value);
    sessionStorage.setItem(key, serializedValue);
  } catch (error) {
    logger.info(`Error setting item in sessionStorage for key "${key}":`, error);
    throw error;
  }
};

export const getSessionItem = <T>(key: string): T | null => {
  try {
    const serializedValue = sessionStorage.getItem(key);
    if (serializedValue === null) {
      // eslint-disable-next-line unicorn/no-null
      return null;
    }
    return JSON.parse(serializedValue) as T;
  } catch (error) {
    logger.info(`Error getting item from sessionStorage for key "${key}":`, error);
    throw error;
  }
};
export const removeSessionItem = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    logger.info(`Error removing item from sessionStorage for key "${key}":`, error);
    throw error;
  }
};

const getCommonParameters = () => {
  return {
    adminId: localStorage.getItem('adminId'),
    studentId: getSessionItem('studentId'),
    sessionId: getSessionItem('sessionId'),
  };
};

export const getAdminCommonHeaders = () => {
  const sessionId = getSessionItem('adminSessionId');
  const adminId = localStorage.getItem('adminId');
  const headers: Record<string, string> = {};
  if (sessionId && typeof sessionId === 'string') {
    headers['session-id'] = sessionId;
  }
  if (adminId) {
    headers['admin-id'] = adminId;
  }
  return headers;
};

export const createKey = (
  base: string,
  parameters: Record<string, string | undefined | null | number> = {},
) => {
  const commonParameters = getCommonParameters();
  const allParameters = { ...parameters, ...commonParameters };
  const filteredParameters = Object.fromEntries(
    Object.entries(allParameters)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, value?.toString() || '']),
  );
  const queryString = new URLSearchParams(filteredParameters).toString();
  return [`${base}?${queryString}`];
};

export const getIconName = (name: string) => {
  const nameParts = name.trim().split(' ') || [];
  const firstNameInitial = nameParts[0]?.[0] ?? '';
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const lastNameInitial = nameParts.at(-1)?.[0] ?? '';
  const iconName = `${firstNameInitial}${lastNameInitial}`.toUpperCase();
  return iconName;
};

export const areObjectsNotEqual = (object1: unknown, object2: unknown) => {
  if (object1 === object2) return false;
  if (
    typeof object1 !== 'object' ||
    typeof object2 !== 'object' ||
    object1 === null ||
    object2 === null
  ) {
    return true;
  }

  const objectA = object1 as Record<string, unknown>;
  const objectB = object2 as Record<string, unknown>;

  const stack: Array<{ obj1: Record<string, unknown>; obj2: Record<string, unknown> }> = [
    { obj1: objectA, obj2: objectB },
  ];

  while (stack.length > 0) {
    const currentPair = stack.pop()!;
    const currentObject1 = currentPair.obj1;
    const currentObject2 = currentPair.obj2;

    const keys1 = Object.keys(currentObject1);
    const keys2 = Object.keys(currentObject2);

    if (keys1.length !== keys2.length) {
      return true;
    }

    for (const key of keys1) {
      if (!Object.prototype.hasOwnProperty.call(currentObject2, key)) {
        return true;
      }

      const value1 = currentObject1[key];
      const value2 = currentObject2[key];

      if (value1 === value2) {
        // eslint-disable-next-line no-continue
        continue;
      }
      if (
        typeof value1 === 'object' &&
        typeof value2 === 'object' &&
        value1 !== null &&
        value2 !== null
      ) {
        stack.push({
          obj1: value1 as Record<string, unknown>,
          obj2: value2 as Record<string, unknown>,
        });
      } else {
        return true;
      }
    }
  }

  return false;
};

export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const newIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[newIndex]] = [shuffled[newIndex], shuffled[index]];
  }
  const isShuffled = !shuffled.every((item, index) => {
    return !areObjectsNotEqual(item, array[index]);
  });

  if (!isShuffled) {
    [shuffled[0], shuffled[1]] = [shuffled[1], shuffled[0]];
  }
  return shuffled;
}

export function arraysAreEqual(array1: string[], array2: string[]): boolean {
  if (array1.length !== array2.length) return false;
  for (const [index, element] of array1.entries()) {
    if (element !== array2[index]) return false;
  }
  return true;
}
export const onStopPropagation = (event: React.MouseEvent<HTMLDivElement>) => {
  event.stopPropagation();
};
